import { useTranslation } from 'react-i18next';
import { type Document } from '@/api/queries/storage';
import { DownloadDisposition, getDownloadUrl } from '@/api/queries/storage';

interface FilePreviewProps {
  document: Document;
}

export function FilePreview({ document }: FilePreviewProps) {
  const { t: tStorage } = useTranslation('storage');
  const previewUrl = getDownloadUrl(
    document.id,
    document.content_version_id,
    document.name,
    DownloadDisposition.INLINE
  );

  switch (document.file_type.toLowerCase()) {
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'webp':
    case 'bmp':
    case 'pnm':
      return (
        <div className="w-full min-h-full bg-gray-100 flex items-center justify-center">
          <img
            src={previewUrl}
            alt={document.name}
            className="max-w-full max-h-[600px] object-contain"
          />
        </div>
      );
    case 'pdf':
      return (
        <div className="w-full h-full bg-gray-100">
          <iframe src={previewUrl} title={document.name} className="w-full h-full" />
        </div>
      );
    case 'mp4':
    case 'webm':
    case 'ogg':
    case 'mov':
    case 'avi':
    case 'mkv':
    case 'm4v':
    case '3gp':
      return (
        <div className="w-full min-h-full bg-gray-100 flex items-center justify-center">
          <video src={previewUrl} controls className="max-w-full object-contain" preload="metadata">
            {tStorage('detail.videoNotSupported')}
          </video>
        </div>
      );
    default:
      // For other file types, show the fallback
      return (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-300 flex items-center justify-center mx-auto mb-4">
              <span className="text-gray-600 font-medium text-sm">
                {document.file_type.toUpperCase()}
              </span>
            </div>
            <p className="text-gray-600">{tStorage('detail.filePreviewNotAvailable')}</p>
          </div>
        </div>
      );
  }
}
